import { useState, useCallback } from 'react'
import { Button } from '@/components/ui/button'
import { VisualToolsOverlay } from '@/components/visual-tools'
import type { VisualToolsState } from '@/hooks/useVisualTools'

// Sample data for each widget type
const SAMPLE_PRESENT_TEXT = {
  text: 'Schadenfreude',
  category: 'vocabulary' as const,
  translation: 'Pleasure derived from another\'s misfortune',
  example: 'He felt a sense of Schadenfreude when his rival failed.',
}

const SAMPLE_SHADOW_PRACTICE = {
  sentence: 'The quick brown fox jumps over the lazy dog.',
  translation: 'El rápido zorro marrón salta sobre el perro perezoso.',
}

const SAMPLE_CORRECTION = {
  incorrect: 'Yesterday I go to the store',
  correct: 'Yesterday I went to the store',
  explanation: 'Use past tense "went" for actions that happened in the past.',
}

const SAMPLE_ACCOMPLISHMENT = {
  message: 'Amazing progress! 🎉',
}

// Auto-dismiss timeouts (same as useVisualTools)
const TIMEOUTS = {
  present: 30000,
  shadow: 30000,
  correction: 15000,
  accomplishment: 10000,
}

const initialState: VisualToolsState = {
  activeType: null,
  presentText: null,
  shadowPractice: null,
  correction: null,
  accomplishment: null,
}

export function VisualToolsTest() {
  const [state, setState] = useState<VisualToolsState>(initialState)
  const [timeoutId, setTimeoutId] = useState<ReturnType<typeof setTimeout> | null>(null)

  const clearDisplay = useCallback(() => {
    if (timeoutId) {
      clearTimeout(timeoutId)
      setTimeoutId(null)
    }
    setState(initialState)
  }, [timeoutId])

  const showWidget = useCallback((type: 'present' | 'shadow' | 'correction' | 'accomplishment') => {
    // Clear any existing timeout
    if (timeoutId) {
      clearTimeout(timeoutId)
    }

    // Set the new state based on type
    const newState: VisualToolsState = {
      activeType: type,
      presentText: type === 'present' ? SAMPLE_PRESENT_TEXT : null,
      shadowPractice: type === 'shadow' ? SAMPLE_SHADOW_PRACTICE : null,
      correction: type === 'correction' ? SAMPLE_CORRECTION : null,
      accomplishment: type === 'accomplishment' ? SAMPLE_ACCOMPLISHMENT : null,
    }
    setState(newState)

    // Set auto-dismiss timeout
    const id = setTimeout(() => {
      setState(initialState)
      setTimeoutId(null)
    }, TIMEOUTS[type])
    setTimeoutId(id)
  }, [timeoutId])

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 p-8">
      <div className="max-w-2xl mx-auto">
        <h1 className="text-2xl font-bold text-slate-900 mb-2">Visual Tools Test</h1>
        <p className="text-slate-600 mb-8">
          Click a button to trigger a widget. It will auto-dismiss after the timeout (same as real sessions).
        </p>

        <div className="grid grid-cols-2 gap-4 mb-8">
          <Button
            onClick={() => showWidget('present')}
            variant="outline"
            className="h-24 flex flex-col gap-2"
          >
            <span className="text-lg">📝 Present Text</span>
            <span className="text-xs text-slate-500">Vocabulary card (30s)</span>
          </Button>

          <Button
            onClick={() => showWidget('shadow')}
            variant="outline"
            className="h-24 flex flex-col gap-2"
          >
            <span className="text-lg">🎤 Shadow Practice</span>
            <span className="text-xs text-slate-500">Repeat sentence (30s)</span>
          </Button>

          <Button
            onClick={() => showWidget('correction')}
            variant="outline"
            className="h-24 flex flex-col gap-2"
          >
            <span className="text-lg">✏️ Correction</span>
            <span className="text-xs text-slate-500">Error correction (15s)</span>
          </Button>

          <Button
            onClick={() => showWidget('accomplishment')}
            variant="outline"
            className="h-24 flex flex-col gap-2"
          >
            <span className="text-lg">🎉 Accomplishment</span>
            <span className="text-xs text-slate-500">Celebration (10s)</span>
          </Button>
        </div>

        <Button
          onClick={clearDisplay}
          variant="ghost"
          className="w-full"
          disabled={!state.activeType}
        >
          Clear Display
        </Button>

        {state.activeType && (
          <p className="text-center text-sm text-slate-500 mt-4">
            Active: <span className="font-medium">{state.activeType}</span> — will auto-dismiss
          </p>
        )}
      </div>

      {/* The actual overlay - same component used in Practice */}
      <VisualToolsOverlay state={state} onClear={clearDisplay} />
    </div>
  )
}
